import { apiUrl } from '../utils/api-url';

export interface ProjectContact {
  accountExec?: string;
  accountExecEmail?: string;
  technicalLead?: string;
  technicalLeadEmail?: string;
}

export interface SnapshotEntry {
  version: number;
  label: string;
  timestamp: string;
  workloadCount: number;
  factCount: number;
  riskCount: number;
  decisionCount: number;
  stakeholderCount: number;
  azureConsumptionMonthly?: number;
  w365LicensingMonthly?: number;
  grandTotalMonthly?: number;
  summary?: string;
}

/**
 * Azure Migrate execution context (orchestrator RFC §4.2). Mutated only via
 * PUT /api/projects/{id}/migrate-context (see migrate-service) — but it MUST stay on this
 * interface: updateProject PATCHes the whole document, so an absent field here would null
 * the block in Cosmos on every project edit.
 */
export interface MigrateContext {
  authMode: string;
  tenantId?: string;
  subscriptionId?: string;
  resourceGroup?: string;
  assessmentProjectName?: string;
  applianceName?: string;
  applianceStatus: string;
  discoveryStartedAt?: string | null;
}

/**
 * One member of a group programme — a tenant/entity with its own wave, commercial
 * disposition and in-scope window (epic #400 A3). Mirrors ProgrammeMember on
 * ProjectDocument. InScopeFrom/To bound WHEN the member participates — an assignment
 * register, describing where each entity is assigned in the programme.
 */
export interface ProgrammeMember {
  tenantId?: string;
  displayName: string;
  wave: number;
  /** Free text, e.g. "order-form", "separate-instrument-until-consolidation". */
  commercialDisposition?: string;
  inScopeFrom?: string | null;
  inScopeTo?: string | null;
}

export interface Project {
  id?: string;
  customerId: string;
  name: string;
  status: string;
  cafPhase: string;
  scope?: string;
  /**
   * Engagement-type identifiers the project has opted in to (e.g.
   * "m365-tenant-security", "mailbox-migration"). Drives which tagged
   * templates the backend scaffolds. Universal (Azure infrastructure
   * migration) templates always scaffold regardless of this list.
   */
  engagementTypes?: string[] | null;
  azureRegion?: string;
  hypervisor?: string;
  vpnType?: string;
  contacts?: ProjectContact;
  snapshots?: SnapshotEntry[];
  migrateContext?: MigrateContext | null;
  /**
   * Programme membership (epic #400 A3) — null for single-tenant engagements. Like
   * migrateContext, it MUST stay on this interface: updateProject PATCHes the whole
   * document, so an absent field here would null the block in Cosmos on every edit.
   */
  programmeMembers?: ProgrammeMember[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface EngagementTypeOption {
  id: string;
  displayName: string;
}

export interface ScaffoldResult {
  scaffolded: number;
  documents: { id: string; title: string; cafPhase: string; status: string }[];
}

export async function listProjects(customerId: string, token: string): Promise<Project[]> {
  const res = await fetch(apiUrl(`/api/projects?customerId=${encodeURIComponent(customerId)}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to list projects: ${res.status}`);
  return res.json();
}

export async function getProject(id: string, customerId: string, token: string): Promise<Project> {
  const res = await fetch(apiUrl(`/api/projects/${id}?customerId=${encodeURIComponent(customerId)}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to get project: ${res.status}`);
  return res.json();
}

export async function createProject(project: Project, token: string): Promise<Project> {
  const res = await fetch(apiUrl('/api/projects'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(project),
  });
  if (!res.ok) throw new Error(`Failed to create project: ${res.status}`);
  return res.json();
}

export async function scaffoldDocuments(projectId: string, customerId: string, token: string): Promise<ScaffoldResult> {
  const res = await fetch(apiUrl(`/api/projects/${projectId}/scaffold?customerId=${encodeURIComponent(customerId)}`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to scaffold: ${res.status}`);
  return res.json();
}

export async function updateProject(project: Project, token: string): Promise<Project> {
  if (!project.id) throw new Error('Cannot update project without id');
  const res = await fetch(apiUrl(`/api/projects/${project.id}`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(project),
  });
  if (!res.ok) throw new Error(`Failed to update project: ${res.status}`);
  return res.json();
}

export async function listEngagementTypes(token: string): Promise<EngagementTypeOption[]> {
  const res = await fetch(apiUrl('/api/engagement-types'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to list engagement types: ${res.status}`);
  return res.json();
}
